import React, {
  InputHTMLAttributes,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  ListItem,
  ListItemText,
} from '@material-ui/core';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';

import { TreeSelectorNodeGroup } from './TreeSelectorNodeGroup';

import useStyles from './styles';
import { useTranslation } from 'react-i18next';
import StoreIcon from '@material-ui/icons/Store';

const TreeSelectorNodeComponent = (
  {
    node,
    value = [],
    invariants = [],
    disableStoresIcon,
    showLoading = true,
    loadingText = '',
    showNoData = true,
    noDataText = '',
    level = 0,
    checkDisabled = (node: any) => false,
    checkVisibility = (node: any) => true,
    onExpanded = (node: any) => {
      // No-op: Default implementation
    },
    onChange = (value: any[], currentlyActive: any, isChecked) => {
      // No-op: Default implementation
    },
    onNextNodesPage = (node: any) => {
      // No-op: Default implementation
    },
    textStyle = null,
    hasCheck = true,
  }: any,
  ref: any,
) => {
  const { t } = useTranslation();

  noDataText = t('app.common.noItemsFound');
  loadingText = t('app.common.loading');

  const innerGroupConfig = {
    showLoading,
    loadingText,
    showNoData,
    noDataText,
    checkDisabled,
    checkVisibility,
  };

  const { text } = node;
  const { hasChildren, children, ...flatNode } = node;

  const classes = useStyles();
  const refChildren = useRef<any>(null);
  const [checked, setChecked] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isMatchingNode = useCallback(
    items => {
      if (!node) return false;
      const isExactMatch = item => node.path === item.path;
      const isPrefixMatch = item =>
        node.path.startsWith(
          `${item.path || ''}${(item.path || '').endsWith('/') ? '' : '/'}`,
        );
      return items.some(item => isExactMatch(item) || isPrefixMatch(item));
    },
    [node],
  );

  const isInvariantNode = useMemo(
    () => isMatchingNode(invariants),
    [isMatchingNode, invariants],
  );

  const isCheckDisabled = useMemo(() => {
    const fn =
      typeof checkDisabled === 'function' ? checkDisabled : () => checkDisabled;
    return isInvariantNode || fn(node);
  }, [checkDisabled, node, isInvariantNode]);

  const isVisible = useMemo(() => {
    const fn =
      typeof checkVisibility === 'function'
        ? checkVisibility
        : () => checkVisibility;
    return fn(node);
  }, [checkVisibility, node]);

  useEffect(() => {
    setChecked(isInvariantNode || isMatchingNode(value));
  }, [value, isMatchingNode, isInvariantNode]);

  const handleClick = () => {
    setExpanded(!expanded);
    onExpanded(node);
  };
  const handleCheck = event => {
    const { children, hasChildren, ...flatNode } = node;
    const elChecked = event?.target?.checked;
    setChecked(elChecked);
    if (elChecked) {
      onChange([flatNode], node, elChecked);
    } else {
      onChange([], node, elChecked);
    }
  };
  const handleChildrenChange = (
    target: any,
    value: any[],
    currentlyActive,
    isChecked,
  ) => {
    const { children, hasChildren, ...flatNode } = node;
    const allChecked = children.every(
      child => value.findIndex(item => item.path === child.path) >= 0,
    );
    let selection: any[] = value;

    const isParentExists = value.findIndex(item =>
      currentlyActive.path.startsWith(item.path),
    );

    if (allChecked && !isCheckDisabled) {
      selection = value.filter(item => !item.path.startsWith(node.path));
      selection.push(flatNode);
    } else if (value.findIndex(item => item.path === node.path) >= 0) {
      selection = [
        ...selection,
        ...children.map(child => {
          const { children, hasChildren, ...flatNode } = child;
          return flatNode;
        }),
      ];
      selection = selection.filter(
        item => item.path !== target.path && item.path !== node.path,
      );
    } else {
      selection = value.filter(item => item.path !== node.path);
    }
    if (
      isParentExists !== -1 &&
      currentlyActive.path.split('/').length - target.path.split('/').length >=
        1 &&
      !isChecked
    ) {
      //Remove the unchecked element, preserving the children of it's parent and removing the parent element
      target = children.filter(item => item.path === target.path);
      selection = [...selection, ...target[0].children].filter(
        item =>
          item.path !== currentlyActive.path &&
          !currentlyActive.path.startsWith(item.path),
      );
    }
    onChange(selection, currentlyActive, isChecked);
  };

  useImperativeHandle(ref, () => ({
    collapseAll: () => {
      refChildren?.current?.collapseAll();
      setExpanded(false);
    },
    expandAll: () => {
      refChildren?.current?.expandAll();
      setExpanded(true);
    },
  }));

  return (
    <>
      {isVisible && (
        <>
          <ListItem button style={{ height: 38 }}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              flexWrap="nowrap"
              justifyContent="stretch"
              flexGrow="1"
            >
              {!disableStoresIcon && node.text !== t('app.common.loading') && (
                <StoreIcon />
              )}
              <Box onClick={handleClick} flexBasis="1" style={{ minWidth: 24 }}>
                {hasChildren && (
                  <>{expanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}</>
                )}
              </Box>
              <Box flexGrow="1" onClick={handleClick}>
                <ListItemText style={textStyle} primary={text} />
              </Box>
              {hasCheck && (
                <Box>
                  <Checkbox
                    className={classes.checkbox}
                    checked={checked}
                    onChange={handleCheck}
                    disabled={isCheckDisabled}
                    inputProps={
                      {
                        'aria-label': `Select ${text}`,
                        'data-testId': `${text}-nodeRegion`,
                      } as InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                </Box>
              )}
            </Box>
          </ListItem>

          {hasChildren && (
            <Collapse in={expanded} timeout="auto">
              <TreeSelectorNodeGroup
                ref={refChildren}
                {...innerGroupConfig}
                parent={node}
                nodes={children}
                value={value}
                invariants={invariants}
                level={level + 1}
                pagination={node.pagination}
                onNodeExpanded={onExpanded}
                onChange={handleChildrenChange}
                onNextNodesPage={onNextNodesPage}
                disablePadding={true}
                disableStoresIcon={disableStoresIcon}
              />
            </Collapse>
          )}
        </>
      )}
    </>
  );
};

export const TreeSelectorNode = memo(forwardRef(TreeSelectorNodeComponent));
